<template>
  <div class="dev-ed">
    <div v-if="mode === 'edit' && device" class="dev-ed__head">
      <span class="dev-ed__tile" aria-hidden="true"><app-icon name="tablet" :size="20" /></span>
      <div class="dev-ed__ident">
        <span class="dev-ed__name">Drucker {{ device.printer_id }}</span>
        <span class="dev-ed__meta" title="Die Drucker-ID kann nicht geändert werden">
          <app-icon name="lock" :size="12" class="dev-ed__lock" />
          <span>Drucker-ID fest</span>
        </span>
        <span v-if="admin && customerText" class="dev-ed__customer" :title="customerText">{{ customerText }}</span>
      </div>
    </div>

    <div v-if="mode === 'create'" class="app-form-field">
      <label class="app-label" :for="ids.printer">Drucker-ID</label>
      <app-text-field
        :id="ids.printer"
        :value="value.printer_id"
        :error="errors.printer_id"
        inputmode="numeric"
        autocomplete="off"
        @input="set('printer_id', $event)"
      />
    </div>
    <div class="app-form-field">
      <label class="app-label" :for="ids.device">Geräte-ID</label>
      <app-text-field
        :id="ids.device"
        :value="value.device_id"
        :error="errors.device_id"
        autocomplete="off"
        autocapitalize="none"
        spellcheck="false"
        @input="set('device_id', $event)"
      />
    </div>
    <div v-if="!admin" class="app-form-field">
      <label class="app-label" :for="ids.provider">Zahlungsanbieter</label>
      <app-select
        :id="ids.provider"
        block
        :value="value.payment_provider"
        :items="providerChoices"
        placeholder="Kein Zahlungsanbieter"
        @input="set('payment_provider', $event)"
      />
    </div>
    <div class="app-form-field">
      <label class="app-label" :for="ids.comment">Bemerkung</label>
      <app-text-field
        :id="ids.comment"
        textarea
        :rows="2"
        :value="value.comment"
        @input="set('comment', $event)"
      />
    </div>

    <!-- Schalterzeilen wie bei Benutzer -->
    <div class="dev-ed__rows">
      <app-switch-row :value="value.active" label="Aktiv" @input="set('active', $event)" />
      <app-switch-row v-if="admin" :value="value.enable_sumup" label="SumUp" @input="set('enable_sumup', $event)" />
      <app-switch-row :value="value.is_test_device" label="Testgerät" @input="set('is_test_device', $event)" />
    </div>

    <section v-if="mode === 'edit' && device" class="dev-ed__section">
      <h3 class="dev-ed__heading">{{ admin ? "Gerät" : "Letzte Meldungen" }}</h3>
      <dl class="dev-ed__list">
        <template v-for="row in infoRows">
          <dt :key="'t-' + row.label" class="dev-ed__label">{{ row.label }}</dt>
          <dd :key="'d-' + row.label" class="dev-ed__value">{{ row.value }}</dd>
        </template>
      </dl>
      <div v-if="!admin">
        <app-button icon="pin" @click="$emit('show-location')">Letzten Standort anzeigen</app-button>
      </div>
    </section>
  </div>
</template>

<script>
// Inhalt des Detailbereichs „Gerät bearbeiten“ bzw. „Gerät anlegen“ (Kundenwunsch 13.10.2026: „wie benutzer mit
// detailansicht zum bearbeiten das bitte auch so bei den geräten machen und nicht mehr inline“). Aufbau wie
// UserEditor: Bearbeiten mit Kopf (Gerät-Kachel, „Drucker 101“, Drucker-ID fest wie bisher in der Tabelle), Felder
// Geräte-ID, Zahlungsanbieter, Bemerkung, Schalter Aktiv und Testgerät, darunter „Letzte Meldungen“ (seit 27.10.2026,
// vorher „Zuletzt gesehen“: letzter Upload, letztes GPS-Signal und letzter App-Start je genau und relativ, App-Version
// und Gerät aus dem letzten App-Start; nur lesen) mit „Letzten Standort anzeigen“ (Ereignis show-location, Karte im
// bisherigen Dialog). Anlegen: zusätzlich die Drucker-ID, ohne „Letzte Meldungen“. Reine Darstellung: der Entwurf
// (value) gehört der Seite; jede Eingabe meldet field(key, wert).
// variant="admin" (Superadmin „Geräte“ aller Kunden, Kundenwunsch 13.10.2026): Kunde im Kopf, Schalter SumUp statt
// Zahlungsanbieter, Abschnitt „Gerät“ mit dem Erstelldatum, ohne Standort.
import { formatDate, formatDateTime, LEER } from "@/utils/format";
import { relativeTime } from "@/utils/relativeTime";

// „27.10.2026, 11:24 · vor 30 Min.“; „heute …“, „gestern …“ und ältere Angaben stünden doppelt neben dem Datum
function meldung(value) {
  if (!present(value)) {
    return LEER;
  }
  const rel = relativeTime(value);
  return formatDateTime(value) + (/^(gerade|vor \d+ (Min|Std))/.test(rel) ? " · " + rel : "");
}

function present(value) {
  return value !== null && value !== undefined && value !== "";
}

export default {
  name: "DeviceEditor",
  props: {
    // Entwurf: { printer_id, device_id, comment, payment_provider, active (Boolean), is_test_device (Boolean) }
    value: { type: Object, required: true },
    // gespeicherter Stand der Zeile; im Anlegen-Modus null
    device: { type: Object, default: null },
    mode: { type: String, default: "edit" },
    // [{ value, text }]
    providers: { type: Array, default: () => [] },
    errors: { type: Object, default: () => ({}) },
    // "customer" (Stammdaten) oder "admin" (Superadmin, alle Kunden)
    variant: { type: String, default: "customer" },
  },
  computed: {
    ids() {
      const base = "dev-ed-" + this._uid;
      return {
        printer: base + "-drucker",
        device: base + "-geraet",
        provider: base + "-anbieter",
        comment: base + "-bemerkung",
      };
    },
    providerChoices() {
      const items = [{ value: null, text: "Kein Zahlungsanbieter" }].concat(this.providers);
      const current = this.value.payment_provider;
      if (present(current) && !items.some((i) => i.value === current)) {
        items.push({ value: current, text: String(current) });
      }
      return items;
    },
    admin() {
      return this.variant === "admin";
    },
    customerText() {
      const d = this.device;
      return d && present(d.customer_id) ? [d.customer_id, d.cust_printname].filter(present).join(" – ") : "";
    },
    infoRows() {
      const d = this.device;
      if (this.admin) {
        return [{ label: "Erstellt", value: present(d.creation_time) ? formatDate(d.creation_time) : LEER }];
      }
      const model = [d.manufacturer, d.model].filter(present).join(" ");
      return [
        { label: "Letzter Upload", value: meldung(d.last_upload) },
        { label: "Letztes GPS-Signal", value: meldung(d.last_gps) },
        { label: "Letzter App-Start", value: meldung(d.last_app_start) },
        { label: "App-Version", value: present(d.app_version) ? String(d.app_version) : LEER },
        { label: "Gerät", value: model ? model + (present(d.android_release) ? " · Android " + d.android_release : "") : LEER },
      ];
    },
  },
  methods: {
    set(key, value) {
      this.$emit("field", key, value);
    },
  },
};
</script>

<style scoped>
.dev-ed {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}
.dev-ed__head {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.dev-ed__tile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 40px;
  height: 40px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface-raised);
  color: var(--ink-2);
}
.dev-ed__ident {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.dev-ed__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.dev-ed__meta {
  display: inline-flex;
  white-space: nowrap;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
.dev-ed__lock {
  flex: none;
}
/* Kunde (Superadmin) als eigene Zeile unter „Drucker-ID fest“ */
.dev-ed__customer {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-2);
}
.dev-ed__rows {
  display: flex;
  flex-direction: column;
}
/* „Zuletzt gesehen“ wie die Abschnitte im Verkaufsdetail */
.dev-ed__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--line-2);
}
h3.dev-ed__heading {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  letter-spacing: .02em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.dev-ed__list {
  display: grid;
  grid-template-columns: 124px minmax(0, 1fr);
  gap: 8px 12px;
  margin: 0;
}
.dev-ed__label {
  font-size: 12px;
  line-height: 20px;
  color: var(--ink-3);
}
.dev-ed__value {
  margin: 0;
  min-width: 0;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}
</style>
